import {
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useLocalStorage } from '@uidotdev/usehooks'
import { useEffect, type ElementType } from 'react'
import { LuBuilding2, LuChartBar, LuGauge, LuLogOut, LuPanelLeft, LuPlus, LuSlidersHorizontal } from 'react-icons/lu'
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router'
import { useOrgLogout, useOrgMe, usePublicConfig } from '~/api/hooks'
import { committeesOffered } from '~/lib/key-modes'
import { useElection, useOrganization, useOrganizations } from '~/api/organizer'
import { ColorModeButton } from '~/components/ColorModeButton'
import { Logo } from '~/components/Logo'
import { orgTabs } from '~/components/organizer/dashboard/tabs'

/**
 * Organizer dashboard shell — mirrors vocdoni-app DashboardShell + Dashboard/Menu:
 * sticky collapsible sidebar (255px / 48px, `dashboard.menu` background), "New organization" button,
 * menu sections, user profile at the bottom, drawer on mobile.
 */

type MenuItem = { label: string; icon: ElementType; to: string; end?: boolean; count?: number }
type MenuSection = { title: string; items: MenuItem[] }

const useMenu = (): MenuSection[] => {
  const match = useMatch('/organizer/orgs/:orgId/*')
  const votingMatch = useMatch('/organizer/orgs/:orgId/votings/:votingId/*')
  const orgId = match?.params.orgId && match.params.orgId !== 'new' ? match.params.orgId : undefined
  const votingId = votingMatch?.params.votingId !== 'new' ? votingMatch?.params.votingId : undefined
  const org = useOrganization(orgId).data?.organization
  const summary = useOrganizations().data?.organizations.find((o) => o.id === orgId)
  const voting = useElection(orgId, votingId).data?.election
  const committees = committeesOffered(usePublicConfig().data)
  const sections: MenuSection[] = [
    { title: 'Organizer', items: [{ label: 'My organizations', icon: LuBuilding2, to: '/organizer', end: true }] },
  ]
  if (orgId) {
    sections.push({
      title: org?.name ?? 'Organization',
      items: orgTabs(orgId, { collectLocation: org?.requirements.collectLocation ?? true, committees }).map((t) => ({
        label: t.label,
        icon: t.icon,
        to: t.to,
        end: t.key === 'votings',
        count: t.key === 'review' ? summary?.counts.inReview : undefined,
      })),
    })
  }
  if (orgId && votingId) {
    const base = `/organizer/orgs/${orgId}/votings/${votingId}`
    sections.push({
      title: voting?.title ?? 'Voting',
      items: [
        { label: 'Voting overview', icon: LuGauge, to: base, end: true },
        { label: 'Results', icon: LuChartBar, to: `${base}/results` },
        { label: 'Voting settings', icon: LuSlidersHorizontal, to: `${base}/settings` },
      ],
    })
  }
  return sections
}

const MenuContent = ({ reduced, onToggleReduced }: { reduced: boolean; onToggleReduced?: () => void }) => {
  const sections = useMenu()
  const { data } = useOrgMe()
  const logout = useOrgLogout()
  const navigate = useNavigate()

  return (
    <Flex direction='column' h='full'>
      <Box p={2}>
        <Flex alignItems='center' justifyContent='space-between' h='47px' mb={2}>
          <Link to='/organizer' aria-label='My organizations'>
            <Logo minimal={reduced} />
          </Link>
          {onToggleReduced && !reduced && (
            <IconButton
              aria-label='Collapse menu'
              colorPalette='gray'
              variant='subtle'
              size='xs'
              onClick={onToggleReduced}
            >
              <LuPanelLeft />
            </IconButton>
          )}
        </Flex>
        {reduced && onToggleReduced && (
          <IconButton
            aria-label='Expand menu'
            colorPalette='gray'
            variant='subtle'
            size='xs'
            mb={2}
            onClick={onToggleReduced}
          >
            <LuPanelLeft />
          </IconButton>
        )}
        <Button asChild w='full' minW={0} mt={2} mb={8} size='xs'>
          <Link to='/organizer/orgs/new' title='New organization'>
            <HStack gap={reduced ? 0 : 2}>
              <Icon as={LuPlus} boxSize={4} />
              {!reduced && <Text as='span'>New organization</Text>}
            </HStack>
          </Link>
        </Button>

        <VStack alignItems='stretch' gap={6}>
          {sections.map((section) => (
            <VStack key={section.title} alignItems='stretch' gap={1}>
              {!reduced && (
                <Text fontSize='xs' color='fg.muted' px={2} textTransform='uppercase' letterSpacing='wider' truncate>
                  {section.title}
                </Text>
              )}
              {section.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} title={reduced ? item.label : undefined}>
                  {({ isActive }) => (
                    <Flex
                      alignItems='center'
                      gap={4}
                      p={2}
                      borderRadius='sm'
                      fontSize='sm'
                      bg={isActive ? 'bg.muted' : undefined}
                      fontWeight={isActive ? 'bold' : 'normal'}
                      _hover={{ bg: 'bg.muted' }}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon as={item.icon} />
                      {!reduced && item.label}
                      {!reduced && Boolean(item.count) && (
                        <Badge ml='auto' size='xs' colorPalette='orange' variant='solid' borderRadius='full'>
                          {item.count}
                        </Badge>
                      )}
                    </Flex>
                  )}
                </NavLink>
              ))}
            </VStack>
          ))}
        </VStack>
      </Box>

      <Box mt='auto' p={2} borderTop='1px solid' borderColor='border'>
        <Flex alignItems='center' justifyContent='space-between' gap={2} direction={reduced ? 'column' : 'row'}>
          {!reduced && (
            <Box minW={0}>
              <Text fontSize='sm' fontWeight='bold' truncate>
                {data?.organizer.name ?? 'Organizer'}
              </Text>
              <Text fontSize='xs' color='fg.muted' truncate>
                {data?.organizer.email ?? ''}
              </Text>
            </Box>
          )}
          <HStack gap={1} flexDirection={reduced ? 'column' : 'row'}>
            <ColorModeButton size='xs' />
            <IconButton
              aria-label='Log out'
              title='Log out'
              variant='ghost'
              colorPalette='gray'
              size='xs'
              loading={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/organizer/login') })}
            >
              <LuLogOut />
            </IconButton>
          </HStack>
        </Flex>
      </Box>
    </Flex>
  )
}

export const OrganizerShell = () => {
  const { open, onOpen, onClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [reducedPref, setReduced] = useLocalStorage('de:dashboard-menu-reduced', false)
  const { pathname } = useLocation()
  const reduced = Boolean(reducedPref) && !isMobile

  // Close the mobile drawer after navigating.
  useEffect(() => onClose(), [pathname, onClose])

  return (
    <Flex minH='100svh' w='full' bg='bg' maxW='max-window-width' margin='0 auto'>
      <Box
        borderRight='1px solid'
        borderRightColor='table.border'
        bgColor='dashboard.menu'
        display={{ base: 'none', md: 'flex' }}
        flexDirection='column'
        position='sticky'
        top={0}
        w={reduced ? 'dashboard-menu.reduced' : 'dashboard-menu.default'}
        h='100dvh'
        zIndex={100}
        transition='width .3s ease'
        flexShrink={0}
      >
        <MenuContent reduced={reduced} onToggleReduced={() => setReduced((prev) => !prev)} />
      </Box>

      <Drawer.Root open={open} placement='start' onOpenChange={({ open: o }) => (!o ? onClose() : undefined)}>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content p={2}>
            <MenuContent reduced={false} />
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      <Flex flex='1 1 0' flexDirection='column' minW={0}>
        <Flex alignItems='center' px={4} pt={3} pb={2} display={{ base: 'flex', md: 'none' }} gap={2}>
          <IconButton aria-label='Open menu' colorPalette='gray' variant='subtle' size='xs' onClick={onOpen}>
            <LuPanelLeft />
          </IconButton>
          <Logo />
        </Flex>
        <Outlet />
      </Flex>
    </Flex>
  )
}
